import React from 'react'
import { styled } from '@mui/material/styles'
import Box from '@zuto/core/src/box'
import {
  ZutoLogoExperimental,
  ZutoLogoMedium,
  QuoteZoneLogoMobile,
  PoweredByZuto,
  MoneySuperMarket
} from '@zuto/icons'
import { SecureDataIcon } from '@zuto/atoms/src/SecureData/SecureDataIcon'
import { KnownSource } from '../../../../src/types/Source'
import TrustPilotHeader from '../../../../src/components/TrustPilotHeader'
import { useDetectAdBlock } from 'adblock-detect-react'
import TrustpilotStaticReviews from '../../../../src/components/TrustpilotStaticReviews'
import { formlessColors } from '@zuto/theme/src/mui/Colors'

export const StyledHeader = styled(Box)(({ theme }) => ({
  boxSizing: 'border-box',
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
  margin: `${theme.spacing(3)} auto`,
  paddingLeft: theme.spacing(5),
  paddingRight: theme.spacing(5),

  '&.clearscore-review-page-header, &.totallymoney-review-page-header': {
    minHeight: 40
  },

  '&.moneysupermarket-review-page-header': {
    minHeight: 72
  },

  '&.formless-cta-journey': {
    borderTop: `8px solid ${formlessColors.brandYellow}`,
    marginTop: 0,
    paddingTop: theme.spacing(3)
  }
}))


export const StyledLogoWrapper = styled(Box)(() => ({
  maxWidth: 752,
  boxSizing: 'border-box',
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between'
}))


const HeaderRightWrapper = styled('div')({})
const HeaderRightWrapperDefault = styled('div')({})
const AdditionalLogoWrapper = styled('div')({})

const styles = {
  display: 'flex',
  alignItems: 'flex-start',
  '&.default': {
    alignItems: 'flex-end'
  },
  '&.additional-logo': {
    alignItems: 'unset'
  },
  HeaderRightWrapperDefault: {
    '&.additional-logo': {
      display: 'flex',
      alignItems: 'center',
      '&.mobile': {
        flexDirection: 'column',
        alignItems: 'flex-end',
        '& > div': {
          marginTop: '5px'
        }
      }
    }
  },
  AdditionalLogoWrapper: {
    alignItems: 'flex-end',
    display: 'flex',
    marginLeft: '20px'
  },
  SecureDataIconWithAdditionalLogoWrapper: {
    alignItems: 'center',
    display: 'flex',
    gap: '10px',
    '&.mobile-additional-logo': {
      flexDirection: 'column',
      alignItems: 'center',
      gap: '10px'
    }
  }
}

interface HeaderProps {
  isTwoPagesJourneyExperiment: boolean
  trustpilotOnClick: () => void
  logo?: React.ReactElement
  logoLabel?: string
  showRightIcon?: boolean
  secureDataIconAtTop?: boolean
  isMobile?: boolean
  additionalLogo?: React.ReactElement
  isReviewPage?: boolean
  source?: KnownSource
  isTrustpilotV1?: boolean
  isInMSMExperiment?: boolean
  isInFormlessCtaJourney?: boolean
}

const Header = ({
  trustpilotOnClick,
  logo,
  logoLabel,
  showRightIcon = true,
  isTwoPagesJourneyExperiment,
  secureDataIconAtTop = false,
  isMobile,
  additionalLogo,
  isReviewPage,
  source,
  isTrustpilotV1 = false,
  isInMSMExperiment = false,
  isInFormlessCtaJourney = false
}: HeaderProps): JSX.Element => {
  const adBlockDetected = useDetectAdBlock()

  const renderLogo = (): JSX.Element | null => {
    if (source === "quotezone" && isMobile) {
      return <QuoteZoneLogoMobile aria-label="QuoteZone Logo" />
    }

    if (!logo) {
      if (!isTrustpilotV1) {
        return (
          <TrustpilotStaticReviews
            onClick={trustpilotOnClick}
            width={80}
            isAboveTheFold
          />
        )
      }
      return <></>
    }

    const filterLogoProps = ({
      id,
      width,
      height,
      viewBox,
      children,
      fill
    }: Record<string, string>) => ({
      id,
      width,
      height,
      viewBox,
      children,
      fill
    })

    const props = {
      ...filterLogoProps(logo.props),
      "data-testid": "header-logo",
      "aria-label": logoLabel
    }

    return React.createElement(logo.type, props)
  }

  const renderRightIcon = () => (showRightIcon ? renderLogo() : <></>)

  const renderAdditionalRightLogo = (): JSX.Element | null => {
    if (!additionalLogo) {
      return <></>
    }
    const props = {
      ...additionalLogo.props,
      "data-testid": "additional-header-logo"
    }
    return React.createElement(additionalLogo.type, props)
  }

  return (
    <>
      <StyledHeader
        as="header"
        className={[
          isReviewPage ? `${source}-review-page-header` : null,
          isInFormlessCtaJourney ? "formless-cta-journey" : null
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <StyledLogoWrapper>
          {isInMSMExperiment ? (
            <Box
              display="flex"
              justifyContent="space-between"
              width="100%"
              alignItems="center"
              mt="6px"
            >
              <MoneySuperMarket data-testid="moneysupermarket-header-logo" />
              <Box
                as="a"
                href="/"
                data-testid="powered-by-zuto-logo"
                mt={0}
                mb={0}
                aria-label="Zuto home"
                height="20px"
              >
                <PoweredByZuto />
              </Box>
            </Box>
          ) : (
            <>
              <Box
                as="a"
                href="/"
                data-testid="zuto-logo"
                aria-label="Zuto home"
                my={0}
                display="block"
                style={{ lineHeight: 0 }}
              >
                {isTwoPagesJourneyExperiment ? (
                  <ZutoLogoExperimental />
                ) : (
                  <ZutoLogoMedium />
                )}
              </Box>
              {secureDataIconAtTop && !isInFormlessCtaJourney ? (
                <HeaderRightWrapper
                  sx={styles}
                  className={!logo ? "default" : ""}
                >
                  <SecureDataIcon
                    variant={!logo && isTrustpilotV1 ? "bold" : "small"}
                    flexDirection="column"
                    alignItems="center"
                    marginRight="16px"
                  />
                  {!additionalLogo && renderRightIcon()}
                  {additionalLogo && (
                    <AdditionalLogoWrapper
                      sx={styles.SecureDataIconWithAdditionalLogoWrapper}
                      className={isMobile ? "mobile-additional-logo" : ""}
                    >
                      {renderRightIcon()}
                      {renderAdditionalRightLogo()}
                    </AdditionalLogoWrapper>
                  )}
                </HeaderRightWrapper>
              ) : (
                <HeaderRightWrapperDefault
                  sx={styles.HeaderRightWrapperDefault}
                  className={[
                    additionalLogo ? "additional-logo" : "",
                    isMobile ? "mobile" : ""
                  ].join(" ")}
                >
                  {renderRightIcon()}
                  {additionalLogo && (
                    <AdditionalLogoWrapper sx={styles.AdditionalLogoWrapper}>
                      {renderAdditionalRightLogo()}
                    </AdditionalLogoWrapper>
                  )}
                </HeaderRightWrapperDefault>
              )}
            </>
          )}
        </StyledLogoWrapper>
      </StyledHeader>
      {isTrustpilotV1 && <TrustPilotHeader adBlockDetected={adBlockDetected} />}
    </>
  )
}

export default Header
